import React, { useEffect, useState } from "react";
import ActivityFormWrapper from "../../../components/activities/ActivityFormWrapper";
import {
  ActivityMeta,
  ActivityType,
  ILanguageFillter,
  ILesson,
  ILevel,
} from "../../../types";
import Layout from "@components/layout";
import {
  fetchLessonsByLevel,
  listLevels,
} from "src/service/activities.services";
import showToast from "@utils/toast";
import CodeChip from "@components/code-chip";
import { Video, VideoApiResponse } from "src/pages/view-videos/ListVideocard";
import {
  fetchAllLanguages,
  getRequest,
} from "src/service/api-services/api-services";
import { API_URL } from "src/service/config";
import {
  AudioResponse,
  IAudio,
} from "src/pages/view-audio-assets/ViewAudioAssets";
import {
  currentLanguage,
  currentLessonInfo,
  currentLevelInfo,
  getFromMemory,
} from "@utils/storage.utils";

// const currentLevel = currentLevelInfo();
// const currentLesson = currentLessonInfo();

const supportedActivityTypes: { label: string; value: ActivityType }[] = [
  { label: "Video Content", value: "VideoContentActivity" },
  { label: "Select Image", value: "SelectCorrectImageActivity" },
  { label: "Tap what you Hear", value: "TapInWhatYouHear" },
  { label: "Fill the Gap", value: "FillTheGapActivity" },
  { label: "Match The Correct Word", value: "MatchTheCorrectWord" },
  { label: "Cross word match match", value: "CrossWordActivity" },
];

const LessonCreationForm = () => {
  const [lessonMeta, setLessonMeta] = useState<ActivityMeta>({
    language: "",
    level: "",
    lesson: "",
  });

  const [currentLevel, setCurrentLevel] = useState<any>(null);
  const [currentLesson, setCurrentLesson] = useState<any>(null);
  const [activityType, setActivityType] = useState<ActivityType>(
    "VideoContentActivity"
  );
  const [activityData, setActivityData] = useState<any>(null);

  useEffect(() => {
    const level = currentLevelInfo();
    const lesson = currentLessonInfo();

    if (!currentLanguage || !level?.level || !lesson?.lessonId) {
      showToast("Activity meta data must be provided!", false);
      return;
    }

    setCurrentLevel(level);
    setCurrentLesson(lesson);
    setLessonMeta({
      language: currentLanguage,
      level: level.levelId,
      lesson: lesson.lessonId,
    });
  }, []);

  return (
    <Layout>
      <div className="flex flex-col lg:flex-row h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-md p-8 space-y-8">
            <div className="text-center space-y-1">
              <h1 className="text-2xl font-bold text-gray-800">
                Add Lesson Activity
              </h1>
              <p className="text-sm text-gray-500">
                Define language, level, and select an activity type
              </p>
            </div>

            {currentLevel && currentLesson && (
              <div className="space-y-6">
                <h2 className="text-base font-semibold text-gray-700 border-b pb-2">
                  Metadata
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Language</label>
                    <input
                      type="text"
                      name="language"
                      disabled
                      value={lessonMeta.language}
                      required
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Level</label>
                    <input
                      type="text"
                      disabled
                      value={`${currentLevel.level} - ${currentLevel.title}`}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="form-label">Lesson</label>
                    <input
                      type="text"
                      disabled
                      value={`${currentLesson.lessonNumber} - ${currentLesson.title}`}
                      required
                      className="form-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <h2 className="text-base font-semibold text-gray-700 border-b pb-2">
                Select Activity Type
              </h2>
              <div className="flex overflow-x-auto gap-2 scrollbar-hide pb-1">
                {supportedActivityTypes.map(({ label, value }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setActivityType(value);
                      setActivityData(null);
                    }}
                    className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap border transition ${
                      activityType === value
                        ? "bg-brand text-white shadow-sm border-transparent"
                        : "bg-gray-50 text-gray-800 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-6 border-t">
              <ActivityFormWrapper
                activityType={activityType}
                lessonMeta={lessonMeta}
              />

              {activityData && (
                <p className="text-sm text-brand pt-3">
                  Activity data ready for submission.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right pane */}
        {/* <div className="hidden lg:block w-full lg:w-[400px] border-t lg:border-t-0 lg:border-l border-gray-200 p-4 bg-gray-50"> */}
        <div className="hidden lg:block w-full lg:w-[400px] border-t lg:border-t-0 lg:border-l border-gray-200 p-4 bg-gray-50 overflow-y-auto h-screen">
          <h2 className="text-lg font-semibold mb-2">Asset Search</h2>
          <GraphicPane />
          <AudioPane />
          <VideoPane />
        </div>
      </div>
    </Layout>
  );
};

const RightPaneAccordionItem = ({ title, children }: any) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border rounded-lg bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center px-4 py-2 text-left font-medium text-gray-800 hover:bg-gray-50"
      >
        <span>{title}</span>
        <span>{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && (
        <div className="px-4 py-3 border-t bg-gray-50">{children}</div>
      )}
    </div>
  );
};

const AudioPane = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [audios, setAudios] = useState<IAudio[]>([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query) params.append("search", query);
        params.append("limit", "5");

        const response = await getRequest<AudioResponse>(
          `${API_URL}/audios?${params.toString()}`
        );
        setAudios(response.result.audios);
        setError(undefined);
      } catch (error) {
        setError(error instanceof Error ? error.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (!query) {
      setAudios([]);
      setError("Please enter a search term");
      setLoading(false);
      return;
    }

    fetchVideos();
  }, [query]);

  return (
    <RightPaneAccordionItem title="Audio">
      <input
        className="w-full md:w-1/2 px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand"
        placeholder="Search for a audios..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {loading && (
        <p className="text-center text-gray-500">Loading audios...</p>
      )}

      {!loading && error && <p className="text-center text-red-500">{error}</p>}

      {!loading && !error && audios.length === 0 && (
        <p className="text-center text-gray-600">No audios available.</p>
      )}

      {!loading && !error && audios.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {audios.map((audio) => (
            <AudioCard
              key={audio.id}
              code={audio.code}
              audioUrl={audio.url}
              localText={audio.localText}
              translation={audio.translation}
            />
          ))}
        </div>
      )}
    </RightPaneAccordionItem>
  );
};

const GraphicPane = () => {
  return (
    <RightPaneAccordionItem title="Graphic">
      <div className="grid grid-cols-2 gap-3">
        <GraphicCard code="222111" url="" title="Users" description="1,245" />
        <GraphicCard
          code="333222"
          url="https://lams-assets.s3.us-east-1.amazonaws.com/graphics/000603.png?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Credential=AKIARHGH6LINAZJSZNWB%2F20250803%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250803T225117Z&X-Amz-Expires=3600&X-Amz-Signature=d16ba0a83c58a8ff270adc4376749c6db9986a91de25ab9ff9cb91d0f989aa82&X-Amz-SignedHeaders=host&x-amz-checksum-mode=ENABLED&x-id=GetObject"
          title=""
          description="34"
        />
      </div>
    </RightPaneAccordionItem>
  );
};

const VideoPane = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [videos, setVideos] = useState<Video[]>([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query) params.append("search", query);
        params.append("limit", "5");

        const response = await getRequest<VideoApiResponse>(
          `${API_URL}/videos?${params.toString()}`
        );
        setVideos(response.result.videos);
        setError(undefined);
      } catch (error) {
        setError(error instanceof Error ? error.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (!query) {
      setVideos([]);
      setError("Please enter a search term");
      setLoading(false);
      return;
    }
    fetchVideos();
  }, [query]);

  return (
    <RightPaneAccordionItem title="Video">
      <input
        className="w-full md:w-1/2 px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand"
        placeholder="Search for a audios..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {loading && (
        <p className="text-center text-gray-500">Loading videos...</p>
      )}

      {!loading && error && <p className="text-center text-red-500">{error}</p>}

      {!loading && !error && videos.length === 0 && (
        <p className="text-center text-gray-600">No videos available.</p>
      )}
      {!loading && !error && videos.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {videos.map((video) => (
            <VidoCard
              key={video.id}
              code={video.code}
              videoUrl={video.url}
              localText={video.translation}
              translation={video.translation}
            />
          ))}
        </div>
      )}
    </RightPaneAccordionItem>
  );
};

const Card = ({ title, value }: { title: string; value: any }) => (
  <div className="bg-white border rounded-lg p-4 shadow text-center">
    <p className="text-gray-500 text-sm">{title}</p>
    <p className="text-xl font-bold">{value}</p>
  </div>
);

const GraphicCard = ({
  code,
  url,
  title,
  description,
}: {
  code: string;
  url: string;
  title: string;
  description: any;
}) => (
  <article className="relative w-full max-w-sm bg-white rounded-2xl shadow-md p-4 flex flex-col justify-between">
    <CodeChip code={code} />
    <div className="w-full h-48 bg-gray-100 border border-gray-300 rounded-lg flex items-center justify-center overflow-hidden">
      <img src={url} alt={title} className="object-contain w-full h-full" />
    </div>
    <div className="text-sm mb-2">
      <p className="font-medium text-black line-clamp-2">{title}</p>
      <p className="text-gray-500 line-clamp-2">{description}</p>
    </div>
  </article>
);

const VidoCard = ({
  code,
  videoUrl,
  localText,
  translation,
}: {
  code: string;
  videoUrl: string;
  localText: string;
  translation: string;
}) => (
  <article className="bg-white border rounded-lg p-4 shadow text-center">
    <CodeChip code={code} />
    <div className="aspect-video w-full rounded-md overflow-hidden mb-2">
      <video controls className="w-full h-full object-contain">
        <source src={videoUrl} type="video/mp4" />
      </video>
    </div>

    <div className="text-sm mb-2">
      <p className="font-medium text-black line-clamp-2">{localText}</p>
      <p className="text-gray-500 line-clamp-2">{translation}</p>
    </div>
  </article>
);

const AudioCard = ({
  code,
  audioUrl,
  localText,
  translation,
}: {
  code: string;
  audioUrl: string;
  localText: string;
  translation: string;
}) => (
  <article className="bg-white border rounded-lg p-4 shadow text-center">
    <div className="mb-3">
      <CodeChip code={code} />
    </div>

    <audio controls className="w-full rounded">
      <source src={audioUrl} type="audio/mp3" />
      Your browser does not support the audio element.
    </audio>

    <div className="text-sm mt-3">
      <p className="font-medium text-gray-800 line-clamp-1">{localText}</p>
      <p className="text-gray-500 line-clamp-1">{translation}</p>
    </div>
  </article>
);

export default LessonCreationForm;
